import { useId } from '@react-aria/utils'
import { Button } from '@/components/Button'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardContentFooterActions,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import flattenChildren from 'react-keyed-flatten-children'
import { Icon, type IconName } from '@/components/Icon'
import { SectionHeadline } from '@/components/SectionHeadline'
import type ConsultationManagerViewModel from '../../ConsultationManagerViewModel'
import { observer } from 'mobx-react-lite'
import { match } from '@/utils/pattern-match-util'
import type { Consultation } from '@/features/jobs/consultations/Consultation'
import { TranscriptDownloadDropdownContainer } from '@/features/jobs/details/consultation/components/TranscriptDownloadDropdown'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { type ReactNode, useMemo } from 'react'
import { useObservableValue } from '@/hooks/rx-hooks'
import { mediaQueryMatchStream } from '@/utils/browser-util'

type AppointmentSectionVariation = 'default' | 'call-ready' | 'completed'

interface AppointmentSectionProps {
  headline: string
  title: string
  description: string
  variation?: AppointmentSectionVariation
  children: ReactNode
  testId?: string
}

/**
 * Appointment Section
 */
export function AppointmentSection({
  headline,
  title,
  description,
  variation = 'default',
  children,
  testId,
}: AppointmentSectionProps) {
  const headlineId = useId()
  const iconName: IconName = match(variation, {
    default: () => 'calendar-date',
    'call-ready': () => 'call-start-call',
    completed: () => 'calendar-tick',
  })

  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>{headline}</SectionHeadline>
      <Card>
        <CardContent>
          <CardContentDecoration>
            <Icon name={iconName} variant="surface-background" />
          </CardContentDecoration>
          <CardTitle>{title}</CardTitle>
          <CardSubtitle>{description}</CardSubtitle>
          {children}
        </CardContent>
      </Card>
    </PageSection>
  )
}

interface AppointmentSectionContainerProps {
  vm: ConsultationManagerViewModel
}

/**
 * Appointment Section Container
 */
export const AppointmentSectionContainer = observer(
  function AppointmentSectionContainer({
    vm,
  }: AppointmentSectionContainerProps) {
    const testId = 'appointment-section'
    const stackActions = useObservableValue(
      useMemo(() => mediaQueryMatchStream('screen and (max-width: 480px)'), []),
    )
    const consultation = vm.consultationSchedulerVm.consultation
    if (!consultation) {
      return null
    }

    const { title, description, headline, variation, actions } =
      getDisplayStageState(consultation, vm)

    return (
      <AppointmentSection
        variation={variation}
        headline={headline}
        title={title}
        description={description}
        testId={testId}
      >
        <CardContentFooterActions
          stacked={stackActions && flattenChildren(actions).length > 2}
        >
          {actions}
        </CardContentFooterActions>
      </AppointmentSection>
    )
  },
)

interface DisplayStageState {
  actions: ReactNode
  title: string
  description: string
  headline: string
  variation: AppointmentSectionVariation
}

/**
 * Get the state for the appointment section based on the display stage.
 *
 * @param consultation
 * @param vm
 * @returns
 */
function getDisplayStageState(
  consultation: Consultation,
  vm: ConsultationManagerViewModel,
): DisplayStageState {
  return consultation.match<DisplayStageState>({
    awaitingPickup: (stage) => {
      if (vm.isAwaitingProClaim) {
        return {
          title: 'Waiting Pro assignment',
          description:
            'Your availability selection will be enabled once a Pro has been assigned.',
          headline: 'Your appointment',
          variation: 'default',
          actions: <></>,
        }
      }

      return {
        title:
          stage.availability.length > 0
            ? 'Review your availability'
            : 'Propose a time',
        description:
          stage.availability.length > 0
            ? `You currently set your availability for ${stage.availability.length} appointment slots`
            : 'Provide your availability, and your Pro will pick a mutually convenient time.',
        headline: 'Your appointment',
        variation: 'default',
        actions: (
          <>
            {stage.availability.length > 0 && (
              <Button
                variant="text-inline"
                label="View availability"
                onPress={() => vm.showReviewAvailabilityDialog()}
                testId="view-availability-button"
              />
            )}
            {vm.consultationSchedulerVm.isSettingAvailabilityAllowed && (
              <Button
                variant="text-inline"
                label={
                  stage.availability.length > 0
                    ? 'Edit availability'
                    : 'Set availability'
                }
                onPress={() => vm.showSetAvailabilityDialog()}
                testId="set-availability-button"
              />
            )}
          </>
        ),
      }
    },
    awaitingProConfirmation: (stage) => ({
      title: `Awaiting your Pro's confirmation`,
      description: `You submitted ${stage.availability.length} available times. Your Pro will review and confirm an appointment shortly.`,
      headline: 'Your appointment',
      variation: 'default',
      actions: (
        <>
          {stage.availability.length > 0 && (
            <Button
              variant="text-inline"
              label="View availability"
              onPress={() => vm.showReviewAvailabilityDialog()}
              testId="view-availability-button"
            />
          )}
          {vm.consultationSchedulerVm.isSettingAvailabilityAllowed && (
            <Button
              variant="text-inline"
              label={
                stage.availability.length > 0
                  ? 'Edit availability'
                  : 'Set availability'
              }
              onPress={() => vm.showSetAvailabilityDialog()}
              testId="set-availability-button"
            />
          )}
        </>
      ),
    }),
    booked: (stage) => ({
      title: 'Appointment confirmed',
      description: `${stage.bookedTime.toStartingDayString()} at ${stage.bookedTime.toStartingHourString()} for ${stage.requestedLength} minutes`,
      headline: 'Your appointment',
      variation: 'default',
      actions: (
        <>
          <Button
            variant="text-inline"
            label="Add to calendar"
            onPress={() => vm.downloadConsultationCalendar()}
            isPending={vm.downloadConsultationCalendar.pending}
            testId="add-to-calendar-button"
          />

          {vm.consultationSchedulerVm.isSettingAvailabilityAllowed && (
            <Button
              variant="text-inline"
              label="Reschedule"
              onPress={() => vm.showSetAvailabilityDialog()}
              testId="reschedule-button"
            />
          )}
          <Button
            variant="text-inline"
            label="Join call"
            onPress={vm.joinRoom}
            isPending={vm.joinRoom.pending}
            testId="join-call-button"
          />
        </>
      ),
    }),
    dayOfCall: (stage) => ({
      title: 'View appointment',
      description: `Today at ${stage.bookedTime.toStartingHourString()} for ${stage.requestedLength} minutes`,
      headline: 'Your appointment',
      variation: 'default',
      actions: (
        <>
          <Button
            variant="text-inline"
            label="Join call"
            onPress={vm.joinRoom}
            isPending={vm.joinRoom.pending}
            testId="join-call-button"
          />
        </>
      ),
    }),
    inProgress: () => ({
      title: 'Meet with your Pro',
      description:
        'Arrange the best time to meet in chat. When ready, join here.',
      headline: 'Your appointment',
      variation: 'call-ready',
      actions: (
        <>
          <Button
            variant="text-inline"
            label="Join call"
            onPress={vm.joinRoom}
            isPending={vm.joinRoom.pending}
            testId="join-call-button"
          />
        </>
      ),
    }),
    callCompleted: (stage) => ({
      title: 'Call completed',
      description: `Your ${stage.requestedLength} minute consultation was held with ${stage.providerDisplayName} on ${stage.bookedTime.toStartingDayString()} at ${stage.bookedTime.toStartingHourString()}`,
      headline: 'Your appointment',
      variation: 'completed',
      actions: (
        <>
          <TranscriptDownloadDropdownContainer vm={vm} />
        </>
      ),
    }),
    rescheduleNeeded: () => ({
      title: 'Action required - update your availability',
      description: `Your previously submitted times have passed. Submit new availability and your Pro will confirm a time that works for both of you.`,
      headline: 'Your appointment',
      variation: 'default',
      actions: (
        <>
          <Button
            variant="text-inline"
            label="Update availability"
            onPress={() => vm.showSetAvailabilityDialog()}
            testId="update-availability-button"
          />
        </>
      ),
    }),
  })
}
